import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getTechniquesJudo,
  getJudoTechniquesWithoutSelfDefense,
} from "../../../../../services/techniquesServices";

import {
  DEFAULT_QUIZZ_UI_STATE,
  STORAGE_KEY,
  buildDynamicOptions,
  buildQuestions,
  calculatePercent,
  getStoredQuizzState,
} from "../utils/techniquesQuizzUtils";

export function useTechniquesQuizzDashboard() {
  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ui, setUi] = useState(() => getStoredQuizzState());

  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState([]);
  const [finished, setFinished] = useState(false);

  const persistUi = useCallback((nextUi) => {
    const safeUi = {
      ...DEFAULT_QUIZZ_UI_STATE,
      ...nextUi,
    };

    setUi(safeUi);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safeUi));
    } catch {
      // localStorage indisponible
    }
  }, []);

  const updateUi = useCallback(
    (patch) => {
      persistUi({
        ...ui,
        ...patch,
      });
    },
    [persistUi, ui]
  );

  const loadTechniques = useCallback(async () => {
    try {
      setLoading(true);

      const data = ui.includeSelfDefense
        ? await getTechniquesJudo()
        : await getJudoTechniquesWithoutSelfDefense();

      setTechniques(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement techniques quizz :", error);
      toast.error("Erreur lors du chargement des techniques.");
      setTechniques([]);
    } finally {
      setLoading(false);
    }
  }, [ui.includeSelfDefense]);

  useEffect(() => {
    loadTechniques();
  }, [loadTechniques]);

  const dynamicOptions = useMemo(
    () => buildDynamicOptions(techniques),
    [techniques]
  );

  const generateQuestions = useCallback(() => {
    const nextQuestions = buildQuestions(techniques, {
      grade: ui.grade,
      count: Number(ui.count || DEFAULT_QUIZZ_UI_STATE.count),
      wrongCount: 3,
    });

    setQuestions(nextQuestions);
    setCurrent(0);
    setSelected(null);
    setScore(0);
    setErrors([]);
    setFinished(false);
  }, [techniques, ui.grade, ui.count]);

  useEffect(() => {
    if (loading) return;

    generateQuestions();
  }, [loading, generateQuestions]);

  useEffect(() => {
    if (!dynamicOptions.grades.includes(ui.grade)) {
      persistUi({
        ...ui,
        grade: DEFAULT_QUIZZ_UI_STATE.grade,
      });
    }
  }, [dynamicOptions.grades, persistUi, ui]);

  const currentQuestion = questions[current] || null;

  const answerQuestion = useCallback(
    (option) => {
      if (!currentQuestion || selected) return;

      setSelected(option);

      if (option === currentQuestion.correctAnswer) {
        setScore((previous) => previous + 1);
      } else {
        setErrors((previous) => [
          ...previous,
          {
            q: currentQuestion,
            selected: option,
          },
        ]);
      }
    },
    [currentQuestion, selected]
  );

  const nextQuestion = useCallback(() => {
    if (!selected) return;

    if (current >= questions.length - 1) {
      setFinished(true);
      return;
    }

    setCurrent((previous) => previous + 1);
    setSelected(null);
  }, [selected, current, questions.length]);

  const restart = useCallback(() => {
    generateQuestions();
  }, [generateQuestions]);

  const progress = useMemo(() => {
    const total = Math.max(questions.length, 1);
    const answeredCount = Math.min(current + (selected ? 1 : 0), total);

    return (answeredCount / total) * 100;
  }, [current, selected, questions.length]);

  const stats = useMemo(() => {
    const total = questions.length;
    const answered = Math.min(current + (selected ? 1 : 0), total);
    const wrong = errors.length;
    const percent = calculatePercent(score, total);

    return {
      totalTechniques: techniques.length,
      totalQuestions: total,
      answered,
      score,
      wrong,
      percent,
      current: total > 0 ? current + 1 : 0,
    };
  }, [techniques.length, questions.length, current, selected, score, errors.length]);

  return {
    loading,
    techniques,
    questions,
    currentQuestion,
    current,
    selected,
    score,
    errors,
    finished,
    progress,
    stats,

    ui,
    dynamicOptions,
    updateUi,

    answerQuestion,
    nextQuestion,
    restart,
    refresh: loadTechniques,
  };
}

export default useTechniquesQuizzDashboard;